/* eslint-disable react-refresh/only-export-components */
import type {ReactElement} from 'react';
import {useContext, useMemo} from 'react';
import type {Component} from 'grapesjs';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {useComponentProp} from '@EmailMakerReact/lib/email-maker-component-style-library';
import type {EmailMakerControl, EmailMakerControlProps} from '../../right-panel-registry.types';
import type {ModListDescriptor, ModListDescriptorFactory, ModListItem} from './mod-list-control.types';
import {MOD_LIST_NO_PROPERTY} from './mod-list-control.types';
import {ModListItem as ModListItemComponent} from './components/mod-list-item/mod-list-item';
import styles from './mod-list-control.module.scss';

interface ModListControlInnerProps extends EmailMakerControlProps {
  descriptorOrFactory: ModListDescriptor | ModListDescriptorFactory;
}

function ModListControlInner({component, descriptorOrFactory}: ModListControlInnerProps): ReactElement {
  const {editor} = useContext(emailMakerContext);

  const descriptor = useMemo((): ModListDescriptor => {
    if (typeof descriptorOrFactory === 'function') {
      if (!editor) {
        return {listClass: '', items: []};
      }

      return descriptorOrFactory(editor, component);
    }

    return descriptorOrFactory;
  }, [descriptorOrFactory, editor, component]);

  return (
    <div className={styles.wrapper}>
      <ul className={`mod-list-container list-two ${descriptor.listClass} ${styles.list}`}>
        {descriptor.items.map((item: ModListItem) => (
          <ModListItemWrapper component={component} item={item} key={item.label} />
        ))}
      </ul>
    </div>
  );
}

/** Wrapper so each item can call useComponentProp without violating the rules of hooks. */
interface ModListItemWrapperProps {
  component: Component;
  item: ModListItem;
}

function ModListItemWrapper({component, item}: ModListItemWrapperProps): ReactElement {
  const propValue = useComponentProp(component, item.property ?? MOD_LIST_NO_PROPERTY);
  const isSelected = Boolean(item.property && propValue);

  return <ModListItemComponent isSelected={isSelected} item={item} />;
}

export function makeModListControl(descriptorOrFactory: ModListDescriptor | ModListDescriptorFactory): EmailMakerControl {
  return function ModListControl(props: EmailMakerControlProps): ReactElement {
    return <ModListControlInner {...props} descriptorOrFactory={descriptorOrFactory} />;
  };
}
